import React, { useState, useEffect, useCallback, useMemo } from 'react';
import logger from '../../utils/logger';
import { useParams, useNavigate } from 'react-router-dom';
import ReactMarkdown from 'react-markdown';
import {
  ArrowLeftIcon,
  BookOpenIcon,
  PrinterIcon,
  MagnifyingGlassIcon,
  ChevronRightIcon,
  HomeIcon,
} from '@heroicons/react/24/outline';

const GuideViewer = () => {
  const { guideId } = useParams();
  const navigate = useNavigate();
  const [content, setContent] = useState('');
  const [loading, setLoading] = useState(true);
  const [tableOfContents, setTableOfContents] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [activeSection, setActiveSection] = useState('');

  const guides = useMemo(() => ({
    'service-advisor': {
      title: 'Service Advisor Guide',
      file: '/guides/01_SERVICE_ADVISOR_GUIDE.md',
      color: 'blue',
    },
    'mechanic': {
      title: 'Mechanic Guide',
      file: '/guides/02_MECHANIC_GUIDE.md',
      color: 'green',
    },
    'finance': {
      title: 'Finance Guide',
      file: '/guides/03_FINANCE_GUIDE.md',
      color: 'yellow',
    },
    'procurement': {
      title: 'Procurement Guide',
      file: '/guides/04_PROCUREMENT_GUIDE.md',
      color: 'purple',
    },
  }), []);

  const currentGuide = useMemo(() => guides[guideId], [guides, guideId]);

  const extractTableOfContents = useCallback((markdown) => {
    const headings = [];
    const lines = markdown.split('\n');

    lines.forEach((line, index) => {
      // Match markdown headings (##, ###, etc.)
      const match = line.match(/^(#{2,3})\s+(.+)/);
      if (match) {
        const level = match[1].length;
        const title = match[2];
        const id = title
          .toLowerCase()
          .replace(/[^\w\s-]/g, '')
          .replace(/\s+/g, '-');

        headings.push({
          level,
          title,
          id,
          line: index,
        });
      }
    });

    setTableOfContents(headings);
  }, []);

  const loadGuide = useCallback(async () => {
    if (!currentGuide) return;

    try {
      setLoading(true);
      const response = await fetch(currentGuide.file);
      const text = await response.text();
      setContent(text);
      extractTableOfContents(text);
      setLoading(false);
    } catch (error) {
      logger.error('Error loading guide:', error);
      setLoading(false);
    }
  }, [currentGuide, extractTableOfContents]);

  useEffect(() => {
    if (currentGuide) {
      loadGuide();
    } else {
      navigate('/help');
    }
  }, [guideId, currentGuide, loadGuide, navigate]);

  const handlePrint = () => {
    window.print();
  };

  const scrollToSection = (id) => {
    const element = document.getElementById(id);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth', block: 'start' });
      setActiveSection(id);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <p className="text-gray-600">Loading guide...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white border-b border-gray-200 print:hidden sticky top-0 z-10 shadow-sm">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            {/* Left: Back button and breadcrumb */}
            <div className="flex items-center space-x-4">
              <button
                onClick={() => navigate('/help')}
                className="inline-flex items-center text-sm text-gray-600 hover:text-gray-900"
              >
                <ArrowLeftIcon className="h-5 w-5 mr-2" />
                Back to Help Center
              </button>
              <div className="hidden sm:flex items-center text-sm text-gray-500">
                <HomeIcon className="h-4 w-4 mr-1" />
                <ChevronRightIcon className="h-4 w-4 mx-2" />
                <span>{currentGuide?.title}</span>
              </div>
            </div>

            {/* Right: Actions */}
            <div className="flex items-center space-x-2">
              <button
                onClick={handlePrint}
                className="inline-flex items-center px-3 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 bg-white hover:bg-gray-50"
              >
                <PrinterIcon className="h-4 w-4 mr-2" />
                Print
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="flex flex-col lg:flex-row gap-8">
          {/* Table of Contents - Sidebar */}
          <aside className="lg:w-64 flex-shrink-0 print:hidden">
            <div className="sticky top-24">
              <div className="bg-white rounded-lg shadow-sm p-4">
                <h3 className="font-semibold text-gray-900 mb-4 flex items-center">
                  <BookOpenIcon className="h-5 w-5 mr-2" />
                  Table of Contents
                </h3>

                {/* Search within guide */}
                <div className="mb-4">
                  <div className="relative">
                    <MagnifyingGlassIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
                    <input
                      type="text"
                      placeholder="Search in guide..."
                      value={searchTerm}
                      onChange={(e) => setSearchTerm(e.target.value)}
                      className="w-full pl-9 pr-3 py-2 text-sm border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
                    />
                  </div>
                </div>

                <nav className="space-y-1 max-h-[calc(100vh-300px)] overflow-y-auto">
                  {tableOfContents.map((heading, index) => (
                    <button
                      key={index}
                      onClick={() => scrollToSection(heading.id)}
                      className={`block w-full text-left px-3 py-2 text-sm rounded-md transition-colors ${
                        heading.level === 2
                          ? 'font-medium text-gray-900 hover:bg-gray-100'
                          : 'pl-6 text-gray-600 hover:bg-gray-50'
                      } ${activeSection === heading.id ? 'bg-blue-50 text-blue-700' : ''}`}
                    >
                      {heading.title}
                    </button>
                  ))}
                </nav>
              </div>
            </div>
          </aside>

          {/* Guide Content */}
          <main className="flex-1 bg-white rounded-lg shadow-sm p-8 print:shadow-none print:p-0">
            {/* Guide Header */}
            <div className="mb-8 pb-6 border-b border-gray-200">
              <h1 className="text-3xl font-bold text-gray-900 mb-2">
                {currentGuide?.title}
              </h1>
              <p className="text-gray-600">
                Comprehensive user guide for {currentGuide?.title.replace(' Guide', '')} role
              </p>
            </div>

            {/* Markdown Content */}
            <div className="prose prose-sm sm:prose lg:prose-lg max-w-none">
              <ReactMarkdown
                components={{
                  // Custom rendering for headings to add IDs
                  h1: ({ node, children, ...props }) => (
                    <h1 className="text-3xl font-bold text-gray-900 mt-8 mb-4" {...props}>
                      {children}
                    </h1>
                  ),
                  h2: ({ node, children, ...props }) => {
                    const id = children
                      .toString()
                      .toLowerCase()
                      .replace(/[^\w\s-]/g, '')
                      .replace(/\s+/g, '-');
                    return (
                      <h2
                        id={id}
                        className="text-2xl font-bold text-gray-900 mt-8 mb-4 scroll-mt-24"
                        {...props}
                      >
                        {children}
                      </h2>
                    );
                  },
                  h3: ({ node, children, ...props }) => {
                    const id = children
                      .toString()
                      .toLowerCase()
                      .replace(/[^\w\s-]/g, '')
                      .replace(/\s+/g, '-');
                    return (
                      <h3
                        id={id}
                        className="text-xl font-semibold text-gray-800 mt-6 mb-3 scroll-mt-24"
                        {...props}
                      >
                        {children}
                      </h3>
                    );
                  },
                  h4: ({ node, children, ...props }) => (
                    <h4 className="text-lg font-semibold text-gray-800 mt-4 mb-2" {...props}>
                      {children}
                    </h4>
                  ),
                  p: ({ node, children, ...props }) => (
                    <p className="text-gray-700 leading-relaxed mb-4" {...props}>
                      {children}
                    </p>
                  ),
                  ul: ({ node, children, ...props }) => (
                    <ul className="list-disc list-inside space-y-2 mb-4 text-gray-700" {...props}>
                      {children}
                    </ul>
                  ),
                  ol: ({ node, children, ...props }) => (
                    <ol className="list-decimal list-inside space-y-2 mb-4 text-gray-700" {...props}>
                      {children}
                    </ol>
                  ),
                  li: ({ node, children, ...props }) => (
                    <li className="ml-4" {...props}>
                      {children}
                    </li>
                  ),
                  table: ({ node, children, ...props }) => (
                    <div className="overflow-x-auto mb-6">
                      <table className="min-w-full divide-y divide-gray-200 border border-gray-300" {...props}>
                        {children}
                      </table>
                    </div>
                  ),
                  thead: ({ node, children, ...props }) => (
                    <thead className="bg-gray-50" {...props}>
                      {children}
                    </thead>
                  ),
                  th: ({ node, children, ...props }) => (
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-700 uppercase tracking-wider" {...props}>
                      {children}
                    </th>
                  ),
                  td: ({ node, children, ...props }) => (
                    <td className="px-4 py-3 text-sm text-gray-700 border-t border-gray-200" {...props}>
                      {children}
                    </td>
                  ),
                  code: ({ node, inline, children, ...props }) => {
                    if (inline) {
                      return (
                        <code className="px-1.5 py-0.5 bg-gray-100 text-red-600 rounded text-sm font-mono" {...props}>
                          {children}
                        </code>
                      );
                    }
                    return (
                      <pre className="bg-gray-50 border border-gray-200 rounded-lg p-4 overflow-x-auto mb-4">
                        <code className="text-sm font-mono text-gray-800" {...props}>
                          {children}
                        </code>
                      </pre>
                    );
                  },
                  blockquote: ({ node, children, ...props }) => (
                    <blockquote className="border-l-4 border-blue-500 pl-4 py-2 my-4 bg-blue-50 text-gray-700 italic" {...props}>
                      {children}
                    </blockquote>
                  ),
                  strong: ({ node, children, ...props }) => (
                    <strong className="font-semibold text-gray-900" {...props}>
                      {children}
                    </strong>
                  ),
                  em: ({ node, children, ...props }) => (
                    <em className="italic text-gray-700" {...props}>
                      {children}
                    </em>
                  ),
                  a: ({ node, children, href, ...props }) => (
                    <a
                      href={href}
                      className="text-blue-600 hover:text-blue-800 underline"
                      target="_blank"
                      rel="noopener noreferrer"
                      {...props}
                    >
                      {children}
                    </a>
                  ),
                  hr: ({ node, ...props }) => (
                    <hr className="my-8 border-t border-gray-300" {...props} />
                  ),
                }}
              >
                {content}
              </ReactMarkdown>
            </div>

            {/* Footer */}
            <div className="mt-12 pt-6 border-t border-gray-200 print:hidden">
              <div className="flex flex-col sm:flex-row items-center justify-between gap-4">
                <p className="text-sm text-gray-600">
                  Was this guide helpful? Let us know how we can improve.
                </p>
                <div className="flex gap-3">
                  <button className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50">
                    Report Issue
                  </button>
                  <button className="px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-md hover:bg-blue-700">
                    Provide Feedback
                  </button>
                </div>
              </div>
            </div>
          </main>
        </div>
      </div>

      {/* Print Styles */}
      <style>{`
        @media print {
          .print\\:hidden {
            display: none !important;
          }
          .print\\:shadow-none {
            box-shadow: none !important;
          }
          .print\\:p-0 {
            padding: 0 !important;
          }
          body {
            background: white;
          }
          .prose {
            max-width: 100% !important;
          }
        }
      `}</style>
    </div>
  );
};

export default GuideViewer;
